import { AudioManager } from "../../AudioManager";
import { GameConstants } from "../../GameConstants";
import { GameVars } from "../../GameVars";
import { Tile } from "./Tile";
import * as Mahjong from "@ravalmatic/mahjong-engine";
import { UndoContainer } from "./UndoContainer";

export class BoardContainer extends Phaser.GameObjects.Container {

    public static currentInstance: BoardContainer;
    public static readonly TILE_WIDTH = 38;
    public static readonly TILE_HEIGHT = 50;

    public tiles: Tile[] = [];
    public tileA: Tile;
    public tileB: Tile;
    
    private combo: number;
    private matchFX: Phaser.GameObjects.Sprite;
    private rescaling: boolean;
    private undoTile1: Tile;
    private undoTile2: Tile;

    constructor(scene: Phaser.Scene) {
        
        super(scene);

        BoardContainer.currentInstance = this;

        this.rescaling = false;
        this.combo = 1;
        this.undoTile1 = null;
        this.undoTile2 = null;
    }

    public update(): void {
        
        for (let i = 0; i < this.tiles.length; i++) {
            this.tiles[i].update();
        }
    }

    public startGame(undo?: boolean): void {

        const boardData = GameVars.mahjongEngine.boardData;

        this.tiles = [];

        for (let l = 0; l < boardData.levels; l++) {
            for (let c = 0; c < boardData.cols; c++) {
                for (let r = 0; r < boardData.rows; r++) {
                    if (boardData.tiles[l][c] && boardData.tiles[l][c][r]) {
                        this.createTile(c, r, l, boardData.tiles[l][c][r]);
                    }
                }
            }

            this.sortTiles(l);
        }

        this.positionBoard();

        if (!undo) {

            const delay = GameVars.levelReset ? 0 : 3000;

            this.tiles.forEach(t => {
    
                t.alpha = 0;
    
                this.scene.tweens.add({
                    targets: t,
                    alpha: 1,
                    duration: 350,
                    delay: delay + 25 * t.p.c,
                    ease: Phaser.Math.Easing.Cubic.Out
                });
            });
    
            this.scene.time.delayedCall(delay, function(): void {
                AudioManager.playSound("tiles_appear");
            }, [], this);
        }
    }

    public undo(tileA: Mahjong.TileData, tileB: Mahjong.TileData): void {

        this.each(function(tile: Tile): void {
            tile.destroy();
        }, this);

        this.startGame(true);
        this.showTilesSelectability(true);

        // OCULTAMOS LAS FICHAS QUE SE VAN A RESTITUIR
        this.undoTile1 = this.getTileObject(tileA);
        this.undoTile2 = this.getTileObject(tileB);

        this.undoTile1.visible = false;
        this.undoTile2.visible = false;
    }

    public onUndoTilesRestored(): void {

        this.undoTile1.visible = true;
        this.undoTile2.visible = true;
    }

    public onInfoShown(): void {

        this.tiles.forEach(t => {
            t.disable();
        });
    }

    public onInfoHidden(): void {

        console.log("onInfoHidden");

        this.tiles.forEach(t => {
            t.enable();
        });
    }

    public unMarkHints(tileData?: Mahjong.TileData): void {

        if (tileData) {
            const tile = this.getTileObject(tileData);
            tile?.unmarkAsHint();
        } else  {
            // TODAS
            // QUITAR EL HINT SI LO HUBIERE
            for (let i = 0; i < this.tiles.length; i++) {
                if (this.tiles[i].markedAsHint) {
                    this.tiles[i].unmarkAsHint();
                }
            }
        }
    }

    public onGamePausedResumed(): void {

        if (GameVars.paused) {
            for (let i = 0; i < this.tiles.length; i++) {
                this.tiles[i].onGamePaused();
            }
        } else {
            for (let i = 0; i < this.tiles.length; i++) {
                this.tiles[i].onGameResumed();
            }
        }
    }

    public onMatch(ttA: Mahjong.TileData, ttB: Mahjong.TileData, scoreData: {score: number, deltaSore: number, combo: number}): void {

        this.combo = scoreData.combo;

        this.tileA = this.getTileObject(ttA);
        this.tileB = this.getTileObject(ttB);

        let tileAAbove: boolean;

        if (this.tileA.x === this.tileB.x) {
            if (this.tileA.y < this.tileB.y) {
                tileAAbove = false;
            } else {
                tileAAbove = true;
            }
        } else if (this.tileA.x < this.tileB.x) {
            tileAAbove = false;
        } else {
            tileAAbove = true;
        }

        this.tileA.x = (this.tileA.x * this.scaleX + this.x - UndoContainer.currentInstance.x) / GameVars.scaleX;
        this.tileA.y = this.tileA.y * this.scaleY + this.y - UndoContainer.currentInstance.y;
        this.tileA.setScale(this.scale);

        this.tileB.x = (this.tileB.x * this.scaleX + this.x - UndoContainer.currentInstance.x) / GameVars.scaleX;
        this.tileB.y = this.tileB.y * this.scaleY + this.y - UndoContainer.currentInstance.y;
        this.tileB.setScale(this.scale);

        if (tileAAbove) {
            UndoContainer.currentInstance.add(this.tileB);
            UndoContainer.currentInstance.add(this.tileA);
        } else {
            UndoContainer.currentInstance.add(this.tileA);
            UndoContainer.currentInstance.add(this.tileB);
        }

        this.tileA.onMatch(this.tileB);
        this.tileB.onMatch(this.tileA);

        this.tiles.splice(this.tiles.indexOf(this.tileA), 1);
        this.tiles.splice(this.tiles.indexOf(this.tileB), 1);

        this.showTilesSelectability();

        this.positionBoard(true);
    }

    public onLockedTile(tile: Mahjong.TileData): void {
        
        for (let i = 0; i < this.tiles.length; i++) {

            if (this.tiles[i].p.c === tile.p.c && this.tiles[i].p.r === tile.p.r && this.tiles[i].level === tile.l) {
                this.tiles[i].shake();
            }

            const tileData: Mahjong.TileData = {p: {c: this.tiles[i].p.c, r: this.tiles[i].p.r}, l: this.tiles[i].level, type: this.tiles[i].type};

            const isFree = Mahjong.Utils.isTileFree(tileData, GameVars.mahjongEngine.boardData.tiles);

            if (!isFree) {
                this.tiles[i].markAsUnselectable();
            }
        }
    }

    public unMarkUnselectableTiles(): void {

        this.tiles.forEach(t => {
            t.unmarkAsUnselectable();
        });
    }

    public onHint(): void {

        this.tiles.forEach(t => {
            t.unmarkAsSelected();
        });

        GameVars.matchSet.forEach(tileData => {
            const tile = this.getTileObject(tileData);
            tile.markAsHint();
        });
    }

    public markAsSelected(tile: Mahjong.TileData): void {

        const t = this.tiles.find(t => t.p.c === tile.p.c && t.p.r === tile.p.r && t.level === tile.l);

        if (t) {
            t.markAsSelected();
        }
    }

    public unMarkAsSelected(tile: Mahjong.TileData): void {

        const t = this.tiles.find(t => t.p.c === tile.p.c && t.p.r === tile.p.r && t.level === tile.l);

        if (t) {
            t.unmarkAsSelected();
        }
    }

    public onShuffle(): void {

        this.tiles.forEach(t => {
            t.unmarkAsHint();
            t.unmarkAsSelected();
        });

        this.scene.time.delayedCall(1500, function(): void {
            
            const tiles = GameVars.mahjongEngine.boardData.tiles;
            let delay = this.rescaling ? 1250 : 0;

            for (let i = 0; i < this.tiles.length; i++) {

                const l = this.tiles[i].level;
                const c = this.tiles[i].p.c;
                const r = this.tiles[i].p.r;

                this.scene.tweens.add({
                    targets: this.tiles[i],
                    x: this.tiles[i].x - 5 - 10 * l,
                    y: this.tiles[i].y - 10 - 20 * l,
                    duration: 350,
                    delay: c * 15 + delay,
                    ease: Phaser.Math.Easing.Cubic.Out,
                    yoyo: true,
                    onComplete: () => {
                        this.tiles[i].changeType(tiles[l][c][r]);
                    },
                    onCompleteScope: this
                });
            }

            this.scene.time.delayedCall(this.rescaling ? 1250 : 0, function(): void {
                AudioManager.playSound("reshufffle");
            }, [], this);

        }, [], this);
    }

    public isBoardShowingHint(): boolean {

        for (let i = 0; i < this.tiles.length; i++) {
            if (this.tiles[i].markedAsHint) {
                return true;
            }
        }

        return false;
    }

    public onBoardCleared(): void {

        // POR SI HAABIAN PIEZAS ATASCADAS
        this.scene.tweens.add({
            targets: this,
            alpha: 0,
            duration: 500,
            ease: Phaser.Math.Easing.Cubic.Out
        });
    }

    private getTileObject(tileData: Mahjong.TileData): Tile {

        const tile = this.tiles.find(t => t.p.c === tileData.p.c && t.p.r === tileData.p.r && t.level === tileData.l);

        return tile;
    }

    private createTile(c: number, r: number, level: number, type: string) {

        const offsetX = -4;
        const offsetY = -level * 12;

        const tile = new Tile(this.scene, type, {c, r}, level);
        tile.x = BoardContainer.TILE_WIDTH * c + offsetX;
        tile.y = BoardContainer.TILE_HEIGHT * r + offsetY; 
        
        this.add(tile);

        this.tiles.push(tile);
    } 

    private sortTiles(l: number): void {

        const tiles = this.tiles.filter(t => t.level === l);

        tiles.sort((a, b) => a.p.r - b.p.r + a.p.c - b.p.c);

        for (let i = 0; i < tiles.length - 1; i++) {

            const tileA = tiles[i];
            const tileB = tiles[i + 1];

            this.moveAbove(tileB, tileA);
        }
    }

    private positionBoard(tween?: boolean): void { 

        if (GameVars.levelCleared) {
            return;
        }

        let minX = 1e5;
        let maxX = 0;

        this.tiles.forEach(t => {
            minX = Math.min(minX, t.x);
            maxX = Math.max(maxX, t.x);
        });

        minX -= 38;
        maxX += 48;

        const width = maxX - minX;

        let minY = 1e5;
        let maxY = 0;

        this.tiles.forEach(t => {
            minY = Math.min(minY, t.y);
            maxY = Math.max(maxY, t.y);
        });

        minY -= 50;
        maxY += 62;

        const height = maxY - minY;

        const centerX = GameConstants.GAME_WIDTH / 2;
        let centerY = GameConstants.GAME_HEIGHT / 2;

        // la barra d arriba tiene 72 px de alto
        centerY = (GameConstants.GAME_HEIGHT + 72) / 2;

        // si el nivel ya hubiera sido jugado
        if (GameVars.bestTime !== null && GameVars.bestTime > 0) {
            centerY = GameConstants.GAME_HEIGHT / 2 + 18;
        }

        // CALCULAR LA ESCALA
        const availableWidth = GameConstants.GAME_WIDTH * .825 / GameVars.scaleX;
        let availableHeight: number;
        
        if (GameVars.bestTime !== null && GameVars.bestTime > 0) {
            availableHeight = 740; 
        } else {
            availableHeight = 800; 
        }

        // OPTIMIZAR POR ALTURA
        let scale = availableHeight / height;

        if (width * scale > availableWidth) {
            scale = availableWidth / width;
        }

        if (scale > 1.5) {
            scale = 1.35;
        }

        if (tween) {

            // HAY QUE VER SI SUPONE UN CAMBIO RELEVANTE
            const delta = Math.abs(this.x - (-minX * scale * GameVars.scaleX + centerX - width * scale * GameVars.scaleX / 2)) + Math.abs(this.y - (-minY * scale + centerY - height * scale / 2));
            const dS = scale / this.scaleY - 1;

            if ((delta > 125 || dS > .06) && scale > this.scaleY && GameVars.mahjongEngine.boardData.n > 2) {

                this.rescaling = true;

                this.scene.tweens.add({
                    targets: this,
                    scaleX: scale * GameVars.scaleX,
                    scaleY: scale,
                    x: -minX * scale * GameVars.scaleX + centerX - width * scale * GameVars.scaleX / 2,
                    y: -minY * scale + centerY - height * scale / 2,
                    duration: 1250,
                    delay: 1500,
                    ease: Phaser.Math.Easing.Cubic.Out,
                    onComplete: () => {
                        this.rescaling = false;
                    },
                    onCompleteScope: this
                });
            } 

        } else {

            this.scaleX = scale * GameVars.scaleX;
            this.scaleY = scale;
    
            this.x = -minX * scale * GameVars.scaleX + centerX - width * scale * GameVars.scaleX / 2;
            this.y = -minY * scale + centerY - height * scale / 2;
        }
    }

    private showTilesSelectability(undo?: boolean): void {

        if (GameVars.markingUnselectableTiles) {

            for (let i = 0; i < this.tiles.length; i++) {

                const tileData: Mahjong.TileData = {p: {c: this.tiles[i].p.c, r: this.tiles[i].p.r}, l: this.tiles[i].level, type: this.tiles[i].type};
    
                const isFree = Mahjong.Utils.isTileFree(tileData, GameVars.mahjongEngine.boardData.tiles);
    
                if (isFree) {
                    this.tiles[i].unmarkAsUnselectable(undo);
                } else {
                    this.tiles[i].markAsUnselectable(undo);
                }
            } 
        }
    }
}
